import { HttpError, type Identifier } from "ra-core";
import simpleRestProvider from "ra-data-simple-rest";

import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import type { Sale, SalesFormData } from "../../types";
import type { CrmDataProvider } from "../types";
import { API_URL, httpClient } from "./httpClient";

/**
 * The settings page edits the configuration through `EditBase` with the
 * pseudo-resource "configuration" and id 1, so `getOne`/`update` are mapped
 * onto `GET|PUT /api/configuration`.
 */
const CONFIGURATION_RESOURCE = "configuration";
const CONFIGURATION_ID = 1;

type ConfigurationRecord = {
  id: number;
  config: Partial<ConfigurationContextValue>;
};

const encodeId = (id: Identifier) => encodeURIComponent(String(id));

/**
 * The data provider for the PHP backend: `ra-data-simple-rest` for the plain
 * resources plus the custom endpoints of `api/src/Api.php`.
 *
 * @param apiUrl base URL of the API, without trailing slash
 */
export const createDataProvider = (
  apiUrl: string = API_URL,
): CrmDataProvider => {
  const baseDataProvider = simpleRestProvider(apiUrl, httpClient);

  const readConfiguration = async (): Promise<
    Partial<ConfigurationContextValue>
  > => {
    const { json } = await httpClient(`${apiUrl}/configuration`);
    return (json ?? {}) as Partial<ConfigurationContextValue>;
  };

  const writeConfiguration = async (
    config: Partial<ConfigurationContextValue>,
  ): Promise<Partial<ConfigurationContextValue>> => {
    const { json } = await httpClient(`${apiUrl}/configuration`, {
      method: "PUT",
      body: JSON.stringify(config),
    });
    return (json ?? config) as Partial<ConfigurationContextValue>;
  };

  return {
    ...baseDataProvider,

    getOne: async (resource, params) => {
      if (resource === CONFIGURATION_RESOURCE) {
        const config = await readConfiguration();
        return {
          data: { id: CONFIGURATION_ID, config } as ConfigurationRecord as any,
        };
      }
      return baseDataProvider.getOne(resource, params);
    },

    update: async (resource, params) => {
      if (resource === CONFIGURATION_RESOURCE) {
        const config = await writeConfiguration(
          (params.data as { config?: Partial<ConfigurationContextValue> })
            .config ?? {},
        );
        return {
          data: { id: CONFIGURATION_ID, config } as ConfigurationRecord as any,
        };
      }
      return baseDataProvider.update(resource, params);
    },

    salesCreate: async (
      body: SalesFormData,
    ): Promise<Sale & { temporaryPassword?: string }> => {
      // The backend generates the temporary password itself.
      const { password: _password, ...data } = body;
      const { json } = await httpClient(`${apiUrl}/sales`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return json as Sale & { temporaryPassword?: string };
    },

    salesUpdate: async (
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ): Promise<Sale> => {
      const { json } = await httpClient(`${apiUrl}/sales/${encodeId(id)}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      return json as Sale;
    },

    salesResetPassword: async (
      id: Identifier,
    ): Promise<{ temporaryPassword: string }> => {
      const { json } = await httpClient(
        `${apiUrl}/sales/${encodeId(id)}/reset-password`,
        { method: "POST" },
      );
      return { temporaryPassword: String(json?.temporaryPassword ?? "") };
    },

    changePassword: async (data: {
      currentPassword: string;
      newPassword: string;
    }): Promise<boolean> => {
      await httpClient(`${apiUrl}/auth/change-password`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return true;
    },

    getConfiguration: async (): Promise<Partial<ConfigurationContextValue>> => {
      try {
        return await readConfiguration();
      } catch (error) {
        // Not (or no longer) logged in: the defaults are used until the next
        // load. Any other failure is a real problem and must surface.
        if (error instanceof HttpError && error.status === 401) {
          return {};
        }
        throw error;
      }
    },

    updateConfiguration: writeConfiguration,
  };
};

export const dataProvider = createDataProvider();
